<template>
  <div class="md-layout">
    <!-- Individual Chart on modal -->
    <md-dialog :md-active.sync="showIndividualChart" class="big-modal" style="width: 45%;">
      <md-dialog-title style="padding-left: 37px">
        Individual Payers
      </md-dialog-title>

      <md-dialog-content>
        <div class="md-layout-item md-size-100">
          <div class="md-layout">
            <pie-chart
              v-if="individualStatsLoaded"
              :chartdata="individualPayerChart"
              :options="individualPayerChartOptions" />
          </div>
        </div>
      </md-dialog-content>

      <md-dialog-actions>
        <md-button class="md-default pull-right" @click="showIndividualChart=false">Cancel</md-button>
      </md-dialog-actions>
    </md-dialog>

    <!-- Corporate Chart on modal -->
    <md-dialog :md-active.sync="showCorporateChart" class="big-modal" style="width: 45%;">
      <md-dialog-title style="padding-left: 37px">
        Corporate Payers
      </md-dialog-title>

      <md-dialog-content>
        <div class="md-layout-item md-size-100">
          <div class="md-layout">
            <pie-chart
              v-if="corporateStatsLoaded"
              :chartdata="corporatePayerChart"
              :options="corporatePayerChartOptions" />
          </div>
        </div>
      </md-dialog-content>

      <md-dialog-actions>
        <md-button class="md-default pull-right" @click="showCorporateChart=false">Cancel</md-button>
      </md-dialog-actions>
    </md-dialog>

    <!-- Chart Board -->
    <!-- Chart Filters -->
    <div v-if="hasDefaultPermissions" class="md-layout-item md-size-100">
      <md-toolbar
        v-if="$constants.userCan(['report.getreport'])"
      >
        <md-field class="selectGroup">
          <label>Regions</label>
          <md-select v-model="filters.tax_region_id">
            <md-option value>All</md-option>
            <md-option
              v-for="reg in taxRegions"
              :value="reg.id"
              :key="reg.id"
              class="entity_type"
            >{{ reg.name }}</md-option>
          </md-select>
        </md-field>

        <tax-stations-master :regionId="filters.tax_region_id" :modelName="'taxStation'" :currentValue="taxStation" @update:taxStation="taxStation = $event"/>

        <div class="md-layout-item md-small-size-100 md-size-100">
          <div class="md-layout">
            <md-datepicker ref="startDateFilter" v-model="filters.start_date" :md-debounce="$constants.dpBounce" md-immediately class="input-date-style">
              <label>Start Date</label>
            </md-datepicker>

            <md-datepicker ref="endDateFilter" v-model="filters.end_date" :md-debounce="$constants.dpBounce" md-immediately class="input-date-style">
              <label>End Date</label>
            </md-datepicker>
          </div>
        </div>

        <div class="filter-btns">
          <div class="flt-btns">
            <md-button
              :disabled="!filters.tax_region_id && !filters.tax_office_id && !filters.start_date && !filters.end_date"
              style="margin: 0 5px -10px;"
              class="md-danger md-sm"
              @click="resetFilters"
            >Reset</md-button>
            <md-button
              :disabled="!filters.tax_region_id && !filters.tax_office_id && !filters.start_date && !filters.end_date"
              style="margin-bottom: -10px"
              class="md-success md-sm"
              @click="filterStats"
            >Filter</md-button>
          </div>
        </div>
      </md-toolbar>
    </div>

    <!-- Tax Payers -->
    <template v-if="taxPayersRegisteredStatsLoaded || taxPayersRegisteredTrendsLoaded">
      <div class="md-layout md-layout-item md-size-100 chart-layout">
        <div class="md-layout-item md-size-100">
          <label class="switch pull-right">
            <input type="checkbox" v-model="payersTrendsGroupByMonth">
            <div class="slider round">
              <span class="on"><strong>By Year</strong></span>
              <span class="off"><strong>By Month</strong></span>
            </div>
          </label>
        </div>

        <div class="md-layout-item md-size-50">
          <pie-chart
            v-if="taxPayersRegisteredStatsLoaded"
            :chartdata="taxPayersRegisteredChart"
            :options="taxPayersRegisteredChartOptions" />
        </div>

        <div class="md-layout-item md-size-50">
          <span class="bar-chart-label">{{payersTrendsGroupByMonth ? "Monthly Report" : "Yearly Report"}}:</span>
          <bar-chart
            v-if="taxPayersRegisteredTrendsLoaded"
            :chartdata="payersTrendsChart"
            :options="barOptions" />

          <div v-if="!taxPayersRegisteredTrendsLoaded" class="center">
            <p>Loading...</p>
          </div>
        </div>
      </div>
    </template>

    <!-- Returns -->
    <template v-if="taxReturnsFiledStatsLoaded || taxReturnsFiledTrendsLoaded">
      <div class="md-layout md-layout-item md-size-100 chart-layout">
        <div class="md-layout-item md-size-100">
          <label class="switch pull-right">
            <input type="checkbox" v-model="returnsTrendsGroupByMonth">
            <div class="slider round">
              <span class="on"><strong>By Year</strong></span>
              <span class="off"><strong>By Month</strong></span>
            </div>
          </label>
        </div>

        <div class="md-layout-item md-size-50">
          <pie-chart
            v-if="taxReturnsFiledStatsLoaded"
            :chartdata="taxReturnsFiledChart"
            :options="taxReturnsFiledChartOptions" />
        </div>

        <div class="md-layout-item md-size-50">
          <span class="bar-chart-label">{{returnsTrendsGroupByMonth ? "Monthly Report" : "Yearly Report"}}:</span>
          <bar-chart
            v-if="taxReturnsFiledTrendsLoaded"
            :chartdata="returnsTrendsChart"
            :options="barOptions" />
          <div v-if="!taxReturnsFiledTrendsLoaded" class="center">
            <p>Loading...</p>
          </div>
        </div>

        <div v-if="individualReturnsStatsLoaded" class="md-layout-item md-size-50 chart-margin">
          <pie-chart
            :chartdata="individualReturnsChart"
            :options="individualReturnsChartOptions" />
        </div>

        <div v-if="corporateReturnsStatsLoaded" class="md-layout-item md-size-50 chart-margin">
          <pie-chart
            :chartdata="corporateReturnsChart"
            :options="corporateReturnsChartOptions" />
        </div>
      </div>
    </template>

    <!-- Assessments -->
    <template v-if="taxAssessmentsRaisedStatsLoaded || taxAssessmentsRaisedTrendsLoaded">
      <div class="md-layout md-layout-item md-size-100 chart-layout">
        <div class="md-layout-item md-size-100">
          <label class="switch pull-right">
            <input type="checkbox" v-model="assessmentsTrendsGroupByMonth">
            <div class="slider round">
              <span class="on"><strong>By Year</strong></span>
              <span class="off"><strong>By Month</strong></span>
            </div>
          </label>
        </div>

        <div class="md-layout-item md-size-50">
          <pie-chart
            v-if="taxAssessmentsRaisedStatsLoaded"
            :chartdata="taxAssessmentsRaisedChart"
            :options="taxAssessmentsRaisedChartOptions" />
        </div>

        <div class="md-layout-item md-size-50">
          <span class="bar-chart-label">{{assessmentsTrendsGroupByMonth ? "Monthly Report" : "Yearly Report"}}:</span>
          <bar-chart
            v-if="taxAssessmentsRaisedTrendsLoaded"
            :chartdata="assessmentsTrendsChart"
            :options="barOptions" />
          <div v-if="!taxAssessmentsRaisedTrendsLoaded" class="center">
            <p>Loading...</p>
          </div>
        </div>

        <div v-if="individualAssessmentsStatsLoaded" class="md-layout-item md-size-50 chart-margin">
          <pie-chart
            :chartdata="individualAssessmentsChart"
            :options="individualAssessmentsChartOptions" />
        </div>

        <div v-if="corporateAssessmentsStatsLoaded" class="md-layout-item md-size-50 chart-margin">
          <pie-chart
            :chartdata="corporateAssessmentsChart"
            :options="corporateAssessmentsChartOptions" />
        </div>

      </div>
    </template>

    <!-- Data Borad -->
    <div class="md-layout-item md-size-100" v-if="canAccessStats">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon">
            <md-icon>open_with</md-icon>
          </div>
          <div class="page-ttl">
            <h4 class="title">Statistics</h4>
          </div>
        </md-card-header>

        <div class="md-layout">

          <div style="cursor: pointer;" class="md-layout-item md-xsmall-size-100 md-size-33" @click="openIndividualStatsChart" title="Click here to get information in Pie chart">
            <stats-card header-color="green">
              <template slot="header">
                <div class="card-icon">
                  <md-icon>people</md-icon>
                </div>
                <p class="category">Individual Tax Payers</p>
                <h3 class="title">{{$numeral(stats.total_individual).format('0,0')}}</h3>
              </template>
            </stats-card>
          </div>

          <div style="cursor: pointer;" class="md-layout-item md-xsmall-size-100 md-size-33" @click="openCorporateStatsChart" title="Click here to get information in Pie chart">
            <stats-card header-color="green">
              <template slot="header">
                <div class="card-icon">
                  <md-icon>business</md-icon>
                </div>
                <p class="category">Corporate Tax Payers</p>
                <h3 class="title">{{$numeral(stats.total_corporate).format('0,0')}}</h3>
              </template>
            </stats-card>
          </div>

          <div class="md-layout-item md-xsmall-size-100 md-size-33">
            <stats-card header-color="green">
              <template slot="header">
                <div class="card-icon">
                  <md-icon>people_outline</md-icon>
                </div>
                <p class="category">Total Tax Payers</p>
                <h3 class="title">{{$numeral(stats.total_payers).format('0,0')}}</h3>
              </template>
            </stats-card>
          </div>

          <div class="md-layout-item md-xsmall-size-100 md-size-33">
            <stats-card header-color="warning">
              <template slot="header">
                <div class="card-icon">
                  <md-icon>meeting_room</md-icon>
                </div>
                <p class="category">Tax Stations</p>
                <h3 class="title">{{$numeral(stats.stations).format('0,0')}}</h3>
              </template>
            </stats-card>
          </div>

          <div class="md-layout-item md-xsmall-size-100 md-size-33">
            <stats-card header-color="warning">
              <template slot="header">
                <div class="card-icon">
                  <md-icon>₦</md-icon>
                </div>
                <p class="category">Total Debt</p>
                <h3 class="title">
                  &#8358;
                  <strong>{{$numeral(stats.debt).format('0,0.00')}}</strong>
                </h3>
              </template>
            </stats-card>
          </div>
        </div>
      </md-card>
    </div>

    <div class="md-layout-item md-size-100" v-if="canAccessReports">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon">
            <md-icon>open_with</md-icon>
          </div>
          <div class="page-ttl">
            <h4 class="title">Reports</h4>
          </div>
        </md-card-header>

        <div class="md-layout">
          <div class="md-layout-item md-xsmall-size-100 md-size-33">
            <stats-card header-color="green">
              <template slot="header">
                <div class="card-icon">
                  <md-icon>people</md-icon>
                </div>
                <p class="category">Individual Tax Payers(By {{superAdmin ? 'Station' : 'Region'}})</p>
                <h3 class="title">
                  {{$numeral(reports.individualTaxPayers).format('0,0')}}
                </h3>
              </template>
            </stats-card>
          </div>

          <div class="md-layout-item md-xsmall-size-100 md-size-33">
            <stats-card header-color="green">
              <template slot="header">
                <div class="card-icon">
                  <md-icon>business</md-icon>
                </div>
                <p class="category">Corporate Tax Payers(By {{superAdmin ? 'Station' : 'Region'}})</p>
                <h3 class="title">
                  {{$numeral(reports.corporateTaxPayers).format('0,0')}}
                </h3>
              </template>
            </stats-card>
          </div>

          <div class="md-layout-item md-xsmall-size-100 md-size-33">
            <stats-card header-color="warning">
              <template slot="header">
                <div class="card-icon">
                  <md-icon>₦</md-icon>
                </div>
                <p class="category">Total Gross Income</p>
                <h3 class="title">
                  &#8358;
                  <strong>{{$numeral(reports.returns.total_annual_gross_income).format('0,0.00')}}</strong>
                </h3>
              </template>
            </stats-card>
          </div>

          <div class="md-layout-item md-xsmall-size-100 md-size-33">
            <stats-card header-color="warning">
              <template slot="header">
                <div class="card-icon">
                  <md-icon>₦</md-icon>
                </div>
                <p class="category">Total Tax Paid</p>
                <h3 class="title">
                  &#8358;
                  <strong>{{$numeral(reports.returns.total_annual_tax_paid).format('0,0.00')}}</strong>
                </h3>
              </template>
            </stats-card>
          </div>

          <div class="md-layout-item md-xsmall-size-100 md-size-33">
            <stats-card header-color="warning">
              <template slot="header">
                <div class="card-icon">
                  <md-icon>₦</md-icon>
                </div>
                <p class="category">Total Amount Paid (Withholding)</p>
                <h3 class="title">
                  &#8358;
                  <strong>{{$numeral(reports.returns.total_wht_amount_paid).format('0,0.00')}}</strong>
                </h3>
              </template>
            </stats-card>
          </div>

          <div class="md-layout-item md-xsmall-size-100 md-size-33">
            <stats-card header-color="green">
              <template slot="header">
                <div class="card-icon">
                  <md-icon>list</md-icon>
                </div>
                <p class="category">Assessments</p>
                <h3 class="title">
                  {{$numeral(reports.assessments).format('0,0')}}
                </h3>
              </template>
            </stats-card>
          </div>

          <div class="md-layout-item md-xsmall-size-100 md-size-33">
            <stats-card header-color="green">
              <template slot="header">
                <div class="card-icon">
                  <md-icon>account_tree</md-icon>
                </div>
                <p class="category">Total Bill Count</p>
                <h3 class="title">
                  {{$numeral(reports.bills.totalBillsCount).format('0,0')}}
                </h3>
              </template>
            </stats-card>
          </div>

          <div class="md-layout-item md-xsmall-size-100 md-size-33">
            <stats-card header-color="warning">
              <template slot="header">
                <div class="card-icon">
                  <md-icon>₦</md-icon>
                </div>
                <p class="category">Total Sum Of All Bills</p>
                <h3 class="title">
                  &#8358;
                  <strong>{{$numeral(reports.bills.totalSumBills).format('0,0.00')}}</strong>
                </h3>
              </template>
            </stats-card>
          </div>

          <div class="md-layout-item md-xsmall-size-100 md-size-33">
            <stats-card header-color="warning">
              <template slot="header">
                <div class="card-icon">
                  <md-icon>₦</md-icon>
                </div>
                <p class="category">Total Sum Of All Outstanding</p>
                <h3 class="title">
                  &#8358;
                  <strong>{{$numeral(reports.bills.totalSumBillsOutstanding).format('0,0.00')}}</strong>
                </h3>
              </template>
            </stats-card>
          </div>

          <div class="md-layout-item md-xsmall-size-100 md-size-33">
            <stats-card header-color="warning">
              <template slot="header">
                <div class="card-icon">
                  <md-icon>₦</md-icon>
                </div>
                <p class="category">Total Sum Of Bills Paid</p>
                <h3 class="title">
                  &#8358;
                  <strong>{{$numeral(reports.bills.totalSumBillsPayed).format('0,0.00')}}</strong>
                </h3>
              </template>
            </stats-card>
          </div>

          <div class="md-layout-item md-xsmall-size-100 md-size-33">
            <stats-card header-color="warning">
              <template slot="header">
                <div class="card-icon">
                  <md-icon>₦</md-icon>
                </div>
                <p class="category">Total Debts</p>
                <h3 class="title">
                  &#8358;
                  <strong>{{$numeral(reports.debts).format('0,0.00')}}</strong>
                </h3>
              </template>
            </stats-card>
          </div>
        </div>
      </md-card>
    </div>

    <md-card v-if="!canAccessStats && !canAccessReports" class="md-layout-item md-size-100">
      <md-card-content>
        <h4 style="text-align: center">Welcome to the Easy Tax Admin Portal</h4>
      </md-card-content>
    </md-card>
  </div>
</template>

<script>
import { StatsCard } from "@/components";
import constants from "@/constants";
import { isSuperAdmin,
        getTaxStation,
        getRegion,
        camelToSentence,
        orderBy,
        sumOfElements} from "@/lib/utils";
import PieChart from "./charts/PieChart.vue";
import BarChart from "./charts/BarChart.vue";
import ChartJSPluginDatalabels from "chartjs-plugin-datalabels";
import TaxStationsMaster from "@/components/Inputs/TaxStationsMaster";
import numeral from "numeral";

const chartTooltip = {
  callbacks: {
    title: function(tooltipItem, data) {
      const index = Array.isArray(tooltipItem) ? tooltipItem[0]['index'] : tooltipItem.index;
      const datasetIndex = Array.isArray(tooltipItem) ? tooltipItem[0]['datasetIndex'] : tooltipItem.datasetIndex;
      let textNote = data['datasets'][datasetIndex].label;
      textNote = textNote ? textNote + ' - ' + data['labels'][index] : data['labels'][index]
      return textNote;
    },

    label: function(tooltipItem, data) {
      const index = Array.isArray(tooltipItem) ? tooltipItem[0]['index'] : tooltipItem.index;
      const datasetIndex = Array.isArray(tooltipItem) ? tooltipItem[0]['datasetIndex'] : tooltipItem.datasetIndex;
      return numeral(data['datasets'][datasetIndex]['data'][index]).format('0,0');
    }
  },
  backgroundColor: '#333',
  titleFontSize: 12,
  titleFontColor: '#fff',
  bodyFontColor: '#fff',
  bodyFontSize: 12,
  displayColors: false
}

const commonFilters = {
  start_date: "",
  end_date: "",
  tax_region_id: 0,
  tax_office_id: ""
};

const chartColors = {
  orange: '#fb8c00',
  green: '#43a047',
  blue: '#00bcd4',
  grey: '#666666'
}

const nestedChartColors = {
  blue: '#00bcd4',
  grey: '#666666'
}

export default {
  components: {
    StatsCard,
    PieChart,
    BarChart,
    ChartJSPluginDatalabels,
    TaxStationsMaster
  },

  data() {
    return {
      hasDefaultPermissions: false,
      showIndividualChart: false,
      showCorporateChart: false,
      taxRegions: [],
      taxStation: '',
      filters: { ...commonFilters },
      payersTrendsGroupByMonth: false,
      returnsTrendsGroupByMonth: false,
      assessmentsTrendsGroupByMonth: false,
      payersTrendsGroupBy: "year",
      returnsTrendsGroupBy: "year",
      assessmentsTrendsGroupBy: "year",
      taxPayersRegisteredStatsLoaded: false,
      taxPayersRegisteredStats: {
        individuals: 0,
        corporations: 0
      },
      taxReturnsFiledStatsLoaded: false,
      taxReturnsFiledStats: {
        individuals: 0,
        corporations: 0
      },
      taxAssessmentsRaisedStatsLoaded: false,
      taxAssessmentsRaisedStats: {
        individuals: 0,
        corporations: 0
      },
      taxPayersRegisteredTrendsLoaded: false,
      taxPayersRegisteredTrends: {
        individuals: 0,
        corporations: 0
      },
      taxReturnsFiledTrendsLoaded: false,
      taxReturnsFiledTrends: {
        individuals: 0,
        corporations: 0
      },
      taxAssessmentsRaisedTrendsLoaded: false,
      taxAssessmentsRaisedTrends: {
        individuals: 0,
        corporations: 0
      },
      options: null,
      annualTotal: 0,
      dailyTotal: 0,
      monthlyTotal: 0,
      annualCount: 0,
      dailyCount: 0,
      monthlyCount: 0,
      regions: [],
      officeRegion: 'all',
      stats: {},
      reports: {
        debts: 0,
        individualTaxPayers: 0,
        corporateTaxPayers: 0,
        returns: {
          total_annual_gross_income: 0,
          total_annual_tax_paid: 0,
          total_dev_levy: 0,
          projection_gross_income: 0,
          total_wht_amount_paid: 0
        },
        assessments: 0,
        totalRevenue: 0,
        bills: {
          totalBillsCount: 0,
          totalSumBills: 0,
          totalSumBillsOutstanding: 0,
          totalSumBillsPayed: 0,
        }
      },
      superAdmin: false,
      region: {},
      taxStations: [],
      globalChartOptions: {
        tooltips: {
          yAlign: 'bottom',
          xAlign: 'left',
          xPadding: 10,
          yPadding: 10,
          _bodyAlign: 'center',
          _footerAlign: 'center',
          ...chartTooltip
        },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          datalabels: {
            color: '#fff',
            textAlign: "center",
            font: {
              weight: "bold",
              color: '#fff',
              size: 16
            },
            rotation: -40,
            formatter: (value) => {
              return this.$numeral(value).format('0,0')
            }
          }
        }
      },
      barOptions: {
        tooltips: chartTooltip,
        scales: {
          xAxes: [{
            stacked: true,
          }],
          yAxes: [{
            stacked: true,
            ticks: {
              callback: function(val) {
                return numeral(val).format('0,0');
              },
              autoSkip: false,
              maxRotation: 45,
              minRotation: 45
            }
          }]
        },
        legend: {
          display: true
        },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          datalabels: {
            color: '#333',
            textAlign: "center",
            font: {
              weight: "bold",
              size: 14
            },
            rotation: -40,
            formatter: (value) => {
              return this.$numeral(value).format('0,0')
            }
          }
        }
      },
      bgColors: { ...chartColors },
      nestedBgColors: { ...nestedChartColors },
      chartParams: "",
      payerLabels: {
        individual: "Individuals",
        corporate: "Corporations"
      },
      individualStatsLoaded: false,
      individualStats: {
        returnsFiled: 0,
        returnsNotFiled: 0,
        assessmentsRaised: 0,
        assessmentsNotRaised: 0
      },
      corporateStatsLoaded: false,
      corporateStats: {
        returnsFiled: 0,
        returnsNotFiled: 0,
        assessmentsRaised: 0,
        assessmentsNotRaised: 0
      },
      individualReturnsStatsLoaded: false,
      corporateReturnsStatsLoaded: false,
      individualAssessmentsStatsLoaded: false,
      corporateAssessmentsStatsLoaded: false,
      individualReturnsStats: {
        returnsFiled: 0,
        returnsNotFiled: 0
      },
      corporateReturnsStats: {
        returnsFiled: 0,
        returnsNotFiled: 0
      },
      individualAssessmentsStats: {
        assessmentsRaised: 0,
        assessmentsNotRaised: 0
      },
      corporateAssessmentsStats: {
        assessmentsRaised: 0,
        assessmentsNotRaised: 0
      }
    };
  },

  computed: {
    canAccessStats() {
      return constants.userCan(['stats.getoverview']);
    },

    canAccessReports() {
      return this.canAccessStats || constants.userCan(['report.getreport']);
    },

    // Dashboard Improvements
    taxPayersRegisteredChart() {
      const labels = Object.keys(this.taxPayersRegisteredStats).map(title => camelToSentence(title));
      const data = Object.values(this.taxPayersRegisteredStats);

      return {
        labels: labels,
        datasets: [
          {
            data: data,
            backgroundColor: Object.values(this.bgColors)
          }
        ]
      }
    },

    taxPayersRegisteredChartOptions() {
      const total = sumOfElements(Object.values(this.taxPayersRegisteredStats));

      const titleObj = {
        title: {
          display: true,
          text: `Tax Payers Registered (${total})`
        }
      };

      return Object.assign(titleObj, this.globalChartOptions);
    },

    taxReturnsFiledChart() {
      const labels = Object.keys(this.taxReturnsFiledStats).map(title => camelToSentence(title));
      const data = Object.values(this.taxReturnsFiledStats);

      return {
        labels: labels,
        datasets: [
          {
            data: data,
            backgroundColor: Object.values(this.bgColors)
          }
        ]
      }
    },

    taxReturnsFiledChartOptions() {
      const total = sumOfElements(Object.values(this.taxReturnsFiledStats));

      const titleObj = {
        title: {
          display: true,
          text: `Tax Returns Filed (${total})`
        }
      };

      return Object.assign(titleObj, this.globalChartOptions);
    },

    taxAssessmentsRaisedChart() {
      const labels = Object.keys(this.taxAssessmentsRaisedStats).map(title => camelToSentence(title));
      const data = Object.values(this.taxAssessmentsRaisedStats);

      return {
        labels: labels,
        datasets: [
          {
            data: data,
            backgroundColor: Object.values(this.bgColors)
          }
        ]
      }
    },

    taxAssessmentsRaisedChartOptions() {
      const total = sumOfElements(Object.values(this.taxAssessmentsRaisedStats));

      const titleObj = {
        title: {
          display: true,
          text: `Tax Assessments Raised (${total})`
        }
      };

      return Object.assign(titleObj, this.globalChartOptions);
    },

    payersTrendsChart() {
      const individuals = this.taxPayersRegisteredTrends.individuals;
      const corporations = this.taxPayersRegisteredTrends.corporations;
      let labels = [];
      if(individuals.length > corporations.length) {
        labels = individuals.map(lb => lb.time_stamp);
      } else {
        labels = corporations.map(lb => lb.time_stamp);
      }

      return {
        labels: labels,
        datasets: [
          {
            label: this.payerLabels.individual,
            backgroundColor: this.bgColors.orange,
            data: individuals.map(lb => lb.total_count)
          },
          {
            label: this.payerLabels.corporate,
            backgroundColor: this.bgColors.green,
            data: corporations.map(lb => lb.total_count)
          }
        ]
      };
    },

    returnsTrendsChart() {
      const individuals = this.taxReturnsFiledTrends.individuals;
      const corporations = this.taxReturnsFiledTrends.corporations;
      let labels = [];
      if(individuals.length > corporations.length) {
        labels = individuals.map(lb => lb.time_stamp);
      } else {
        labels = corporations.map(lb => lb.time_stamp);
      }

      return {
        labels: labels,
        datasets: [
          {
            label: this.payerLabels.individual,
            backgroundColor: this.bgColors.orange,
            data: individuals.map(lb => lb.total_count)
          },
          {
            label: this.payerLabels.corporate,
            backgroundColor: this.bgColors.green,
            data: corporations.map(lb => lb.total_count)
          }
        ]
      };
    },

    assessmentsTrendsChart() {
      const individuals = this.taxAssessmentsRaisedTrends.individuals;
      const corporations = this.taxAssessmentsRaisedTrends.corporations;
      let labels = [];
      if(individuals.length > corporations.length) {
        labels = individuals.map(lb => lb.time_stamp);
      } else {
        labels = corporations.map(lb => lb.time_stamp);
      }

      return {
        labels: labels,
        datasets: [
          {
            label: this.payerLabels.individual,
            backgroundColor: this.bgColors.orange,
            data: individuals.map(lb => lb.total_count)
          },
          {
            label: this.payerLabels.corporate,
            backgroundColor: this.bgColors.green,
            data: corporations.map(lb => lb.total_count)
          }
        ]
      };
    },

    individualPayerChart() {
      const labels = Object.keys(this.individualStats).map(title => camelToSentence(title));
      const data = Object.values(this.individualStats);

      return {
        labels: labels,
        datasets: [
          {
            data: data,
            backgroundColor: Object.values(this.bgColors)
          }
        ]
      }
    },

    individualPayerChartOptions() {
      const titleObj = {
        title: {
          display: true,
          text: 'Individual Tax Payers'
        }
      };

      return Object.assign(titleObj, this.globalChartOptions);
    },

    corporatePayerChart() {
      const labels = Object.keys(this.corporateStats).map(title => camelToSentence(title));
      const data = Object.values(this.corporateStats);

      return {
        labels: labels,
        datasets: [
          {
            data: data,
            backgroundColor: Object.values(this.bgColors)
          }
        ]
      }
    },

    corporatePayerChartOptions() {
      const titleObj = {
        title: {
          display: true,
          text: 'Corporate Tax Payers'
        }
      };

      return Object.assign(titleObj, this.globalChartOptions);
    },

    individualReturnsChart() {
      const labels = Object.keys(this.individualReturnsStats).map(title => camelToSentence(title));
      const data = Object.values(this.individualReturnsStats);

      return {
        labels: labels,
        datasets: [
          {
            data: data,
            backgroundColor: Object.values(this.nestedBgColors)
          }
        ]
      }
    },

    individualReturnsChartOptions() {
      const titleObj = {
        title: {
          display: true,
          text: `Individual Returns  - Total Payers (${this.taxPayersRegisteredStats.individuals})`
        }
      };

      return Object.assign(titleObj, this.globalChartOptions);
    },

    corporateReturnsChart() {
      const labels = Object.keys(this.corporateReturnsStats).map(title => camelToSentence(title));
      const data = Object.values(this.corporateReturnsStats);

      return {
        labels: labels,
        datasets: [
          {
            data: data,
            backgroundColor: Object.values(this.nestedBgColors)
          }
        ]
      }
    },

    corporateReturnsChartOptions() {
      const titleObj = {
        title: {
          display: true,
          text: `Corporate Returns  - Total Payers (${this.taxPayersRegisteredStats.corporations})`
        }
      };

      return Object.assign(titleObj, this.globalChartOptions);
    },

    individualAssessmentsChart() {
      const labels = Object.keys(this.individualAssessmentsStats).map(title => camelToSentence(title));
      const data = Object.values(this.individualAssessmentsStats);

      return {
        labels: labels,
        datasets: [
          {
            data: data,
            backgroundColor: Object.values(this.nestedBgColors)
          }
        ]
      }
    },

    individualAssessmentsChartOptions() {
      const titleObj = {
        title: {
          display: true,
          text: `Individual Assessments  - Total Payers (${this.totalIndividualPayers})`
        }
      };

      return Object.assign(titleObj, this.globalChartOptions);
    },

    corporateAssessmentsChart() {
      const labels = Object.keys(this.corporateAssessmentsStats).map(title => camelToSentence(title));
      const data = Object.values(this.corporateAssessmentsStats);

      return {
        labels: labels,
        datasets: [
          {
            data: data,
            backgroundColor: Object.values(this.nestedBgColors)
          }
        ]
      }
    },

    corporateAssessmentsChartOptions() {
      const titleObj = {
        title: {
          display: true,
          text: `Corporate Assessments - Total Payers (${this.totalCorporatePayers})`
        }
      };

      return Object.assign(titleObj, this.globalChartOptions);
    },

    totalIndividualPayers() {
      return this.taxPayersRegisteredStats.individuals;
    },

    totalCorporatePayers() {
      return this.taxPayersRegisteredStats.corporations;
    }
  },

  methods: {
    getIndividualReturnsStats() {
      this.individualReturnsStats.returnsFiled = this.taxReturnsFiledStats.individuals
      this.individualReturnsStats.returnsNotFiled = this.totalIndividualPayers - this.individualReturnsStats.returnsFiled;
      this.individualReturnsStatsLoaded = true;
    },

    getCorporateReturnsStats() {
      this.corporateReturnsStats.returnsFiled = this.taxReturnsFiledStats.corporations
      this.corporateReturnsStats.returnsNotFiled = this.totalCorporatePayers - this.corporateReturnsStats.returnsFiled;
      this.corporateReturnsStatsLoaded = true;
    },

    getIndividualAssessmentsStats() {
      this.individualAssessmentsStats.assessmentsRaised = this.taxAssessmentsRaisedStats.individuals
      this.individualAssessmentsStats.assessmentsNotRaised = this.totalIndividualPayers - this.individualAssessmentsStats.assessmentsRaised;
      this.individualAssessmentsStatsLoaded = true;
    },

    getCorporateAssessmentsStats() {
      this.corporateAssessmentsStats.assessmentsRaised = this.taxAssessmentsRaisedStats.corporations
      this.corporateAssessmentsStats.assessmentsNotRaised = this.totalCorporatePayers - this.corporateAssessmentsStats.assessmentsRaised;
      this.corporateAssessmentsStatsLoaded = true;
    },

    openIndividualStatsChart() {
      if(!constants.hasNestedAccess(['stats.gettaxpayerregisteredstats', 'default'])) {
        this.notification(
          'You are not authorized to access',
          "pan_tool",
          "danger"
        );
        return;
      }

      this.individualStatsLoaded = false;
      this.showCorporateChart = false;
      this.showIndividualChart = true;
      this.individualStats.returnsFiled = this.taxReturnsFiledStats.individuals
      this.individualStats.returnsNotFiled = this.taxPayersRegisteredStats.individuals - this.individualStats.returnsFiled;
      this.individualStats.assessmentsRaised = this.taxAssessmentsRaisedStats.individuals
      this.individualStats.assessmentsNotRaised = this.taxPayersRegisteredStats.individuals - this.individualStats.assessmentsRaised;
      this.individualStatsLoaded = true;
    },

    openCorporateStatsChart() {
      if(!constants.hasNestedAccess(['stats.gettaxpayerregisteredstats', 'default'])) {
        this.notification(
          'You are not authorized to access',
          "pan_tool",
          "danger"
        );
        return;
      }
      this.corporateStatsLoaded = false;
      this.showIndividualChart = false;
      this.showCorporateChart = true;
      this.corporateStats.returnsFiled = this.taxReturnsFiledStats.corporations
      this.corporateStats.returnsNotFiled = this.taxPayersRegisteredStats.corporations - this.corporateStats.returnsFiled;
      this.corporateStats.assessmentsRaised = this.taxAssessmentsRaisedStats.corporations
      this.corporateStats.assessmentsNotRaised = this.taxPayersRegisteredStats.corporations - this.corporateStats.assessmentsRaised;
      this.corporateStatsLoaded = true;
    },

    setTaxStationFilter(field, key) {
      const taxOffice = getTaxStation();
      return JSON.stringify({"field": field, "value": taxOffice[key]});
    },

    getStats() {
      const apiUrl = "stats/overview";
      this.$http.post(apiUrl, { method: "GET" }).then(
        response => {
          if (response.data) {
            if (response.data.error) {
              this._handleErrors(response.data);
            } else {
              this.stats = response.data;
            }
          } else {
            this.notification("An Unknown Error Occurred", "error", "danger");
          }
        },
        error => {
          this.notification(error.message, "error", "danger");
        }
      );
    },

    getDebts() {
      const currentTime = this.$moment().format("YYYY-MM-DD HH:mm:ss");
      const objectionDeadline = {"field": "objection_deadline", "value": currentTime, "cond_op": "<="};
      const statusDefault = {"field": "status_id", "value": "17"};
      const filterParams = [];
      const taxStation = this.setTaxStationFilter("bill.agency_code", "agency_code");
      if(taxStation) {
        filterParams.push(taxStation);
      } else {
        const refIds = this.fetchStationReferences("agency_code");
        const stringifyObj = JSON.stringify({"field": "bill.agency_code", "value": refIds});
        filterParams.push(stringifyObj);
      }
      filterParams.push(JSON.stringify(statusDefault));
      filterParams.push(JSON.stringify(objectionDeadline));
      const embeds = ["assessments"];
      const stringifyEmbed = JSON.stringify(embeds);
      const params = `&no_pages=1&embeds=${stringifyEmbed}&filters=[${filterParams}]`;
      const apiUrl = "assessments/groups";

      this.$http.post(apiUrl, { method: "GET", params: params }).then(
        response => {
          if (response.data) {
            if (!response.data.error) {
              let debtsData = response.data.list;
              debtsData.forEach(dbtItem => {
                let total_outstanding = 0;
                if(dbtItem.assessments.length > 0){
                  dbtItem.assessments.forEach(asm => {
                    total_outstanding += parseFloat(asm.total_outstanding);
                  })
                }
                this.reports.debts += total_outstanding;
              });
            }
          }
        }
      );
    },

    getIndividualTaxPayers() {
      let params = '';
      const filters = [];
      const taxStation = this.setTaxStationFilter("taxPayer.preferred_tax_office_id", "id");
      if(taxStation) {
        filters.push(taxStation);
      } else {
        const refIds = this.fetchStationReferences("id");
        const stringifyObj = JSON.stringify({"field": "taxPayer.preferred_tax_office_id", "value": refIds});
        filters.push(stringifyObj);
      }
      params += `&filters=[${filters}]`;
      const apiUrl = "tax/payers/individuals";

      this.$http.post(apiUrl, { method: "GET", params: params }).then(
        response => {
          if (response.data) {
            if (!response.data.error) {
              this.reports.individualTaxPayers = response.data.pagination.total_count;
            }
          }
        }
      );
    },

    getCorporateTaxPayers() {
      let params = '';
      const filters = [];
      const taxStation = this.setTaxStationFilter("taxPayer.preferred_tax_office_id", "id");
      if(taxStation) {
        filters.push(taxStation);
      } else {
        const refIds = this.fetchStationReferences("id");
        const stringifyObj = JSON.stringify({"field": "taxPayer.preferred_tax_office_id", "value": refIds});
        filters.push(stringifyObj);
      }
      params += `&filters=[${filters}]`;
      const apiUrl = "tax/payers/corporations";

      this.$http.post(apiUrl, { method: "GET", params: params }).then(
        response => {
          if (response.data) {
            if (!response.data.error) {
              this.reports.corporateTaxPayers = response.data.pagination.total_count;
            }
          }
        }
      );
    },

    getReturns() {
      const apiUrl = "tax/returns/stats";
      let params = "";
      if(this.region) params = `&tax_region_id=${this.region.id}`;

      const taxStation = this.setTaxStationFilter("tax_office_id", "id");
      if(taxStation) params = taxStation;

      this.$http.post(apiUrl, { method: "GET", params }).then(
        response => {
          if (response.data) {
            if (!response.data.error) {
              this.reports.returns = response.data;
            }
          }
        }
      );
    },

    getAssessments() {
      let params = '';
      const filters = [];
      const taxStation = this.setTaxStationFilter("taxPayer.preferred_tax_office_id", "id");
      if(taxStation) {
        filters.push(taxStation);
      } else {
        const refIds = this.fetchStationReferences("id");
        const stringifyObj = JSON.stringify({"field": "taxPayer.preferred_tax_office_id", "value": refIds});
        filters.push(stringifyObj);
      }
      params += `&filters=[${filters}]`;
      const apiUrl = "assessments/groups";

      this.$http.post(apiUrl, { method: "GET", params: params }).then(
        response => {
          if (response.data) {
            if (!response.data.error) {
              this.reports.assessments = response.data.pagination.total_count;
            }
          }
        }
      );
    },

    getBills() {
      const filters = [];
      const billUnPaidStatus = 25;
      const billPaidStatus = 26;
      const stringifyObj = JSON.stringify({"field": "status_id", "value": [billUnPaidStatus, billPaidStatus]});

      filters.push(stringifyObj);
      const params = `&no_pages=1&filters=[${filters}]`;
      const apiUrl = "tax/bills/stats";

      this.$http.post(apiUrl, { method: "GET", params: `${params}` }).then(
        response => {
          if (response.data) {
            if (!response.data.error) {
              const data = response.data;
              this.reports.bills.totalBillsCount = data.total_count;
              this.reports.bills.totalSumBills = data.total_amount_due;
              this.reports.bills.totalSumBillsPayed = data.total_amount_paid;
              this.reports.bills.totalSumBillsOutstanding = data.total_amount_due - data.total_amount_paid;
            }
          }
        }
      );
    },

    setDefaultRange() {
      const today    = this.$moment();
      const lastYear = this.$moment().startOf('year');
      return {
        startDate: lastYear.format('Y-M-D'),
        endDate: today.format('Y-M-D')
      }
    },

    fetchStationReferences(refType) {
      const refIds = this.taxStations.map(tt => tt[refType]);
      return refIds.filter(x => x !== null);
    },

    setRegionAndStations() {
      let params = "";
      if(!this.superAdmin) {
        params += "&no_pages=1";
      }
      if(this.region) params += `&filters=[{"field": "region_id", "value": ${this.region.id}}]`;

      const apiUrl = "tax/offices";
      this.$http.post(apiUrl, { method: "GET", params: params }).then(
        response => {
          if (response.data) {
            if (!response.data.error) {
              this.taxStations = response.data.list;
              if(this.canAccessReports) {
                this.getDebts();
                this.getIndividualTaxPayers();
                this.getCorporateTaxPayers();
                this.getReturns();
                this.getAssessments();
                this.getBills();
              }
            }
          }
        }
      );
    },

    // Dashboard Improvements Methods
    filterStats() {
      const { filters } = this;
      let params = "";
      const dateFormatString = "YYYY-MM-DD";

      if(filters.start_date && filters.end_date && filters.start_date > filters.end_date) {
        this.notification("Invalid data range", "error", "danger");
        return;
      }

      if(filters.start_date) {
        const startDate = this.$moment(filters.start_date).format(dateFormatString);
        params = `start_date=${startDate}`;
      }

      if(filters.end_date) {
        const endDate = this.$moment(filters.end_date).format(dateFormatString);
        params = `${params}&end_date=${endDate}`;
      }

      if (filters.tax_region_id) {
        params = `${params}&tax_region_id=${filters.tax_region_id}`;
      }

      if (filters.tax_office_id) {
        params = `${params}&tax_office_id=${filters.tax_office_id}`;
      }
      this.chartParams = params;
      this.loadCharts();
    },

    resetFilters() {
      this.$refs.startDateFilter.inputDate = null;
      this.$refs.endDateFilter.inputDate = null;
      this.chartParams = "";
      this.taxStation = "";
      this.filters = { ...commonFilters };
      this.loadCharts();
    },

    getTaxPayersRegisteredStats() {
      // unloading Pie charts of payers, returns and assessments
      this.taxPayersRegisteredStatsLoaded = false;

      this.taxReturnsFiledStatsLoaded = false;
      this.individualReturnsStatsLoaded = false;
      this.corporateReturnsStatsLoaded = false;

      this.taxAssessmentsRaisedStatsLoaded = false;
      this.individualAssessmentsStatsLoaded = false;
      this.corporateAssessmentsStatsLoaded = false;

      const apiUrl = "stats/tax/payers/registered/stats";

      this.$http.post(apiUrl, { method: "GET", params: this.chartParams }).then(
        response => {
          if (response.data) {
            if (!response.data.error) {
              const responseData = response.data;

              this.taxPayersRegisteredStats.individuals = Number(responseData.individuals);
              this.taxPayersRegisteredStats.corporations = Number(responseData.corporations);
              this.taxPayersRegisteredStatsLoaded = true;
            } else {
              this.notification(
                "An internal error occurred with tax payers registered stats",
                "pan_tool",
                "danger"
              );
            }
          }
        }
      );
    },

    getTaxReturnsFiledStats() {
      const apiUrl = "stats/tax/returns/filed/stats";

      this.$http.post(apiUrl, { method: "GET", params: this.chartParams }).then(
        response => {
          if (response.data) {
            if (!response.data.error) {
              const responseData = response.data;

              this.taxReturnsFiledStats.individuals = responseData.individuals.total_count;
              this.taxReturnsFiledStats.corporations = responseData.corporations.total_count;
              this.taxReturnsFiledStatsLoaded = true;

              this.getIndividualReturnsStats();
              this.getCorporateReturnsStats();

            } else {
              this.notification(
                "An internal error occurred with returns filed stats",
                "pan_tool",
                "danger"
              );
            }
          }
        }
      );
    },

    getTaxAssessmentsRaisedStats() {
      const apiUrl = "stats/assessments/raised/stats";

      this.$http.post(apiUrl, { method: "GET", params: this.chartParams }).then(
        response => {
          if (response.data) {
            if (!response.data.error) {
              const responseData = response.data;

              this.taxAssessmentsRaisedStats.individuals = responseData.individuals.total_count;
              this.taxAssessmentsRaisedStats.corporations = responseData.corporations.total_count;
              this.taxAssessmentsRaisedStatsLoaded = true;

              this.getIndividualAssessmentsStats();
              this.getCorporateAssessmentsStats();
            } else {
              this.notification(
                "An internal error occurred with assessments raised stats",
                "pan_tool",
                "danger"
              );
            }
          }
        }
      );
    },

    getTaxPayersRegisteredTrends() {
      this.taxPayersRegisteredTrendsLoaded = false;
      const apiUrl = "stats/tax/payers/registered/trends";
      let customParams = `${this.chartParams}&group_by=${this.payersTrendsGroupBy}`;

      customParams = this.addDateToParams(customParams);

      this.$http.post(apiUrl, { method: "GET", params: customParams }).then(
        response => {
          if (response.data) {
            if (!response.data.error) {
              const responseData = response.data;
              this.taxPayersRegisteredTrends.individuals = responseData.individuals;
              this.taxPayersRegisteredTrends.corporations = responseData.corporations;
              this.taxPayersRegisteredTrendsLoaded = true;
            } else {
              this.notification(
                "An internal error occurred with tax payers registered trends",
                "pan_tool",
                "danger"
              );
            }
          }
        }
      );
    },

    getTaxReturnsFiledTrends() {
      this.taxReturnsFiledTrendsLoaded = false;
      const apiUrl = "stats/tax/returns/filed/trends";
      let customParams = `${this.chartParams}&group_by=${this.returnsTrendsGroupBy}`;

      customParams = this.addDateToParams(customParams);

      this.$http.post(apiUrl, { method: "GET", params: customParams }).then(
        response => {
          if (response.data) {
            if (!response.data.error) {
              const responseData = response.data;
              this.taxReturnsFiledTrends.individuals = responseData.individuals;
              this.taxReturnsFiledTrends.corporations = responseData.corporations;
              this.taxReturnsFiledTrendsLoaded = true;
            } else {
              this.notification(
                "An internal error occurred with returns filed trends",
                "pan_tool",
                "danger"
              );
            }
          }
        }
      );
    },

    getTaxAssessmentsRaisedTrends() {
      this.taxAssessmentsRaisedTrendsLoaded = false;
      const apiUrl = "stats/assessments/raised/trends";
      let customParams = `${this.chartParams}&group_by=${this.assessmentsTrendsGroupBy}`;

      customParams = this.addDateToParams(customParams);

      this.$http.post(apiUrl, { method: "GET", params: customParams }).then(
        response => {
          if (response.data) {
            if (!response.data.error) {
              const responseData = response.data;
              this.taxAssessmentsRaisedTrends.individuals = responseData.individuals;
              this.taxAssessmentsRaisedTrends.corporations = responseData.corporations;
              this.taxAssessmentsRaisedTrendsLoaded = true;
            } else {
              this.notification(
                "An internal error occurred with assessments raised trends",
                "pan_tool",
                "danger"
              );
            }
          }
        }
      );
    },

    getRegions() {
      const sortBy = orderBy('name');
      this.$http
        .post("tax/offices/regions", { method: "GET", params: `no_pages=1${sortBy}` })
        .then(
          response => {
            if (response.data) {
              if (response.data.error) {
                this._handleErrors(response.data);
              } else {
                this.taxRegions = response.data.list;
              }
            } else {
              this.notification("An Unknown Error Occurred", "error", "danger");
            }
          },
          error => {
            this.notification(error.message, "error", "danger");
          }
        );
    },

    setDefaultPermissions() {
      if(!this.hasDefaultPermissions) {
        this.hasDefaultPermissions = true;
      }
    },

    addDateToParams(params) {
      const { start_date, end_date } = this.filters;
      if (start_date) {
        params += `&start_date=${start_date}`
      }
      if (end_date) {
        params += `&end_date=${end_date}`
      }
      return params;
    },

    loadCharts() {
      if(constants.hasNestedAccess(['stats.gettaxpayerregisteredstats', 'default'])) {
        this.setDefaultPermissions();
        this.getTaxPayersRegisteredStats();
      }
      if(constants.hasNestedAccess(['stats.gettaxpayerregisteredtrends', 'default'])) {
        this.setDefaultPermissions();
        this.getTaxPayersRegisteredTrends();
      }
      if(constants.hasNestedAccess(['stats.getreturnfiledtrends', 'default'])) {
        this.setDefaultPermissions();
        this.getTaxReturnsFiledTrends();
      }
      if(constants.hasNestedAccess(['stats.getassessmentraisedtrends', 'default'])) {
        this.setDefaultPermissions();
        this.getTaxAssessmentsRaisedTrends();
      }
    },

    _handleErrors(response) {
      if (response.data) {
        for (let message in response.data) {
          this.notification(response.data[message][0], "pan_tool", "danger");
        }
      } else {
        if(response.code !== 'ACCESS_DENIED') {
          this.notification(response.message, "pan_tool", "danger");
        }
      }
    },

    notification(message, icon, color) {
      this.$notify({
        message: message,
        icon: icon,
        horizontalAlign: "right",
        verticalAlign: "top",
        type: color
      });
    }
  },

  watch: {
    taxStation(val) {
      this.filters.tax_office_id = val;
    },
    payersTrendsGroupByMonth(val) {
      this.payersTrendsGroupBy = val ? "month" : "year";
      this.getTaxPayersRegisteredTrends();
    },
    returnsTrendsGroupByMonth(val) {
      this.returnsTrendsGroupBy = val ? "month" : "year";
      this.getTaxReturnsFiledTrends();
    },
    assessmentsTrendsGroupByMonth(val) {
      this.assessmentsTrendsGroupBy = val ? "month" : "year";
      this.getTaxAssessmentsRaisedTrends();
    },
    taxPayersRegisteredStatsLoaded(val) {
      if(val) {
        if(constants.hasNestedAccess(['stats.getreturnsfiledstats', 'default'])) {
          this.getTaxReturnsFiledStats();
        }
        if(constants.hasNestedAccess(['stats.getassessmentraisedstats', 'default'])) {
          this.getTaxAssessmentsRaisedStats();
        }
      }
    }
  },

  mounted() {
    this.filters.start_date = this.$moment().startOf('year').format("YYYY-MM-DD");
    this.filters.end_date = this.$moment().format("YYYY-MM-DD");

    this.superAdmin = isSuperAdmin();
    this.region = getRegion();
    this.setRegionAndStations();

    if(this.canAccessStats) {
      this.getStats();
    }

    this.getRegions();
    this.loadCharts();
  }
};
</script>

<style scoped>
.md-progress-spinner {
  margin-top: 15px !important;
}

.switch {
  position: relative;
  display: inline-block;
  width: 120px;
  height: 34px;
}

.switch input {
  display:none;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #666666;
  -webkit-transition: .4s;
  transition: .4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 26px;
  width: 26px;
  left: 4px;
  bottom: 4px;
  background-color: #fff;
  -webkit-transition: .4s;
  transition: .4s;
}

input:checked + .slider {
  background-color: #666666;
}

input:focus + .slider {
  box-shadow: 0 0 1px #2196F3;
}

input:checked + .slider:before {
  -webkit-transform: translateX(85px);
  -ms-transform: translateX(85px);
  transform: translateX(85px);
}

/*------ ADDED CSS ---------*/
.on {
  display: none;
}

.on, .off {
  color: white;
  position: absolute;
  transform: translate(-50%,-50%);
  top: 50%;
  left: 50%;
  font-size: 10px;
  font-family: Verdana, sans-serif;
}

input:checked+ .slider .on {
  display: block;
}

input:checked + .slider .off {
  display: none;
}

/*--------- END --------*/

/* Rounded sliders */
.slider.round {
  border-radius: 34px;
}

.slider.round:before {
  border-radius: 50%;
}

.selectGroup {
  display: flex;
  flex-direction: column;
  max-width: 200px;
  padding-left: 0;
  margin-right: 20px;
  margin-top: 8px;
}
.selectGroup label {
  margin-bottom: 10px;
  /* position: static; */
}
.selectGroup select {
  margin-bottom: 10px;
}
.md-toolbar {
  background-color: #fff !important;
}
.page-ttl {
  float: left;
  margin-right: 10px;
}
.filter-btns {
  width: 100%;
  margin-top: 10px;
}
.flt-btns {
  float: right;
}
.btn-lft {
  margin-right: 6px;
}
.center {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
}
.center p {
  color: #00bcd4;
}
.bar-chart-label {
  color: #666666;
  font-weight: 600;
  font-size: 74%;
}
.chart-layout {
  margin: 20px 0 20px 0;
  background: #dddddd;
  padding: 10px;
  border-radius: 10px;
}
.chart-margin{
  margin-top: 20px;
}
</style>
